import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { Trophy, GlobeHemisphereWest, Clock, Compass, LockSimple } from "@phosphor-icons/react";

const BADGE_META = {
  time_traveler_bronze: { label: "Time Traveler I", icon: "⏳" },
  time_traveler_silver: { label: "Time Traveler II", icon: "⏳" },
  time_traveler_gold: { label: "Time Traveler III", icon: "⏳" },
  world_passport_bronze: { label: "World Passport I", icon: "🌍" },
  world_passport_silver: { label: "World Passport II", icon: "🌍" },
  world_passport_gold: { label: "World Passport III", icon: "🌍" },
  globe_trotter: { label: "Globe Trotter", icon: "✈️" },
  atlas_master: { label: "Atlas Master", icon: "🗺️" },
  ancient_scholar: { label: "Ancient Scholar", icon: "📜" },
  medieval_master: { label: "Medieval Master", icon: "⚔️" },
  archivist: { label: "Archivist", icon: "📚" },
  curator: { label: "Curator", icon: "🎖️" },
};

export default function HistoricalProgress() {
  const { user } = useAuth();
  const { flags, loading: flagsLoading } = useSiteFlags();
  const nav = useNavigate();
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.historical_recipes_enabled) { nav("/", { replace: true }); return; }
    if (!user) { nav("/login?next=/historical/progress"); return; }
    (async () => {
      try {
        const { data } = await api.get("/historical/progress");
        setProg(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [user, flags.historical_recipes_enabled, nav, flagsLoading]);

  if (loading) return <div className="p-16 text-center text-rf-ink-soft">Loading…</div>;
  if (!prog) return null;
  const totals = prog.totals || {};
  const cookedCount = (prog.recipes_cooked || []).length;
  const earned = new Set(prog.badges || []);

  return (
    <div className="min-h-screen bg-rf-cream" data-testid="historical-progress-page">
      <PageMeta
        title="Your Historical Cook Progress"
        description="Track your progress cooking through 5000 years of culinary history on Recipe Filer."
        noindex
      />
      <div className="bg-gradient-to-br from-amber-900/90 via-rf-ink to-rf-ink text-rf-cream py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <Link to="/historical" className="text-sm opacity-70 hover:opacity-100 mb-2 inline-block">← Historical Recipes</Link>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Your Historical Cook Journey</h1>
          <p className="text-lg opacity-90 mt-2">Every recipe you cook adds a country, a continent and an era to your journey.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-3xl p-5 text-center border border-rf-ink/5" data-testid="prog-recipes">
            <Trophy size={28} className="mx-auto text-rf-orange mb-2" />
            <p className="font-display font-black text-3xl text-rf-ink">{cookedCount}</p>
            <p className="text-xs uppercase tracking-widest text-rf-ink-soft mt-1">of {totals.recipes_available || "?"} recipes</p>
          </div>
          <div className="bg-white rounded-3xl p-5 text-center border border-rf-ink/5" data-testid="prog-countries">
            <GlobeHemisphereWest size={28} className="mx-auto text-rf-orange mb-2" />
            <p className="font-display font-black text-3xl text-rf-ink">{(prog.countries || []).length}</p>
            <p className="text-xs uppercase tracking-widest text-rf-ink-soft mt-1">of {totals.countries_available || "?"} countries</p>
          </div>
          <div className="bg-white rounded-3xl p-5 text-center border border-rf-ink/5" data-testid="prog-continents">
            <Compass size={28} className="mx-auto text-rf-orange mb-2" />
            <p className="font-display font-black text-3xl text-rf-ink">{(prog.continents || []).length}</p>
            <p className="text-xs uppercase tracking-widest text-rf-ink-soft mt-1">of {totals.continents_available || "?"} continents</p>
          </div>
          <div className="bg-white rounded-3xl p-5 text-center border border-rf-ink/5" data-testid="prog-centuries">
            <Clock size={28} className="mx-auto text-rf-orange mb-2" />
            <p className="font-display font-black text-3xl text-rf-ink">{(prog.centuries || []).length}</p>
            <p className="text-xs uppercase tracking-widest text-rf-ink-soft mt-1">of {totals.centuries_available || "?"} centuries</p>
          </div>
        </div>

        {/* Badge grid */}
        <div className="bg-white rounded-3xl p-6 mb-6 border border-rf-ink/5">
          <h2 className="font-display font-black text-2xl text-rf-ink mb-4">Badges</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="prog-badges">
            {(prog.badge_catalog || []).map((b) => {
              const meta = BADGE_META[b.key] || { label: b.key, icon: "🏅" };
              const has = earned.has(b.key);
              return (
                <div key={b.key} data-testid={`badge-${b.key}`}
                  className={`rounded-2xl p-4 text-center border-2 ${has ? "border-rf-orange bg-rf-amber/20" : "border-rf-ink/10 bg-rf-cream-alt opacity-60"}`}>
                  <div className="text-3xl mb-1 relative">
                    {meta.icon}
                    {!has && <LockSimple size={14} className="absolute -bottom-1 -right-1 text-rf-ink-soft" />}
                  </div>
                  <p className={`font-display font-black text-sm ${has ? "text-rf-ink" : "text-rf-ink-soft"}`}>{meta.label}</p>
                  <p className="text-[10px] text-rf-ink-soft mt-1">{b.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Countries visited */}
        {(prog.countries || []).length > 0 && (
          <div className="bg-white rounded-3xl p-6 border border-rf-ink/5" data-testid="prog-country-list">
            <h2 className="font-display font-black text-2xl text-rf-ink mb-4">Countries you&apos;ve cooked from</h2>
            <div className="flex flex-wrap gap-2">
              {prog.countries.map((c) => (
                <span key={c} className="chip !bg-rf-amber/30 !text-rf-ink">{c}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
